// Storefront trigger-CTA cosmetics.
//
// Two storefront-only enhancements layered on top of the plain
// "Personalizar" button that `index.ts → mountTriggerButton` renders:
//
//   1. applyThemeButtonStyle — read the merchant theme's own primary
//      "add to cart" button and mirror its fill, radius, typography and
//      shadow onto our CTA, so it looks native to the store instead of
//      a generic orange pill.
//   2. mountStickyCta — a fixed bottom-bar copy of the CTA that fades in
//      only once the in-flow button has scrolled out of view.
//
// Both degrade cleanly off the storefront: when no native buy button
// exists (admin "Vista cliente" iframe, standalone demo) the matcher
// returns `false` and the caller skips the sticky bar, leaving just the
// in-flow CTA with its built-in styling.

// Subpath, not the barrel — the barrel reaches schema→zod, which Rollup
// can't tree-shake out of the page-load entry (see bundle-budget.test.ts).
import { escHtml } from '@personalizer/ui-rows/esc';

// Chevron del CTA, inline a propósito: importar `icons.ts` desde el
// entry arrastraría el set completo (~3 kB) a `embed.js`, que se
// descarga en CADA page-view de producto. Ver bundle-budget.test.ts.
const TRIGGER_CHEVRON = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>';


// The theme's primary "add to cart" button.  Ordered most-specific
// (Donde Fue custom theme) → Online Store 2.0 standards → generics.
// `.shopify-payment-button` (Shop Pay / Apple Pay) is deliberately
// excluded below — its branded fill isn't the store's own button look.
const BUY_BUTTON_SELECTOR = [
  '.df-pdp__cta',
  '[data-df-add]',
  'form[action*="/cart/add"] button[name="add"]',
  'form[action*="/cart/add"] button[type="submit"]',
  '.product-form__submit',
  '.product-form__cart-submit',
  'button[name="add"][type="submit"]',
  '[data-add-to-cart]',
].join(',');

export function findNativeBuyButton(doc: Document = document): HTMLElement | null {
  const nodes = doc.querySelectorAll<HTMLElement>(BUY_BUTTON_SELECTOR);
  for (const el of nodes) {
    if (el.closest('.shopify-payment-button, .additional-checkout-buttons')) continue;
    return el;
  }
  return null;
}

/** Alpha channel of a computed colour string. rgb(...) → 1,
 *  rgba(r,g,b,a) → a, 'transparent' / '' → 0. */
function alphaOf(color: string): number {
  if (!color || color === 'transparent') return 0;
  const inner = color.match(/rgba?\(([^)]+)\)/)?.[1];
  if (!inner) return 1;
  const parts = inner.split(',');
  const a = parts[3];
  return parts.length >= 4 && a ? parseFloat(a) : 1;
}

/** Mirror the merchant's primary buy button onto `target`.  Returns
 *  true when a native button was found — i.e. we're on a real
 *  storefront PDP and the caller may also raise the sticky bar. */
export function applyThemeButtonStyle(target: HTMLElement, doc: Document = document): boolean {
  const btn = findNativeBuyButton(doc);
  const view = doc.defaultView;
  if (!btn || !view) return false;

  // Computed style resolves even though the button is display:none
  // (the hide-default-CTA CSS already fired) — colour/border/font/radius
  // don't depend on layout, so the read is valid.
  const cs = view.getComputedStyle(btn);
  const set = (prop: string, val: string | null): void => {
    if (val && val !== 'none') target.style.setProperty(prop, val, 'important');
  };

  // Shape + typography always copy across.
  set('border-radius', cs.borderRadius);
  set('font-family', cs.fontFamily);
  set('font-weight', cs.fontWeight);
  set('letter-spacing', cs.letterSpacing);
  set('text-transform', cs.textTransform);

  // Fill + text colour only when the theme button is a solid fill.
  // Outline / ghost buttons (near-transparent bg) would leave our white
  // label invisible, so we keep our own fill in that case.
  if (alphaOf(cs.backgroundColor) >= 0.5) {
    set('background-color', cs.backgroundColor);
    set('background-image', cs.backgroundImage);
    set('color', cs.color);
  }

  // Border + the theme's own button shadow, for an exact silhouette.
  if (cs.borderStyle !== 'none' && parseFloat(cs.borderWidth) > 0) {
    set('border', `${cs.borderWidth} ${cs.borderStyle} ${cs.borderColor}`);
  }
  set('box-shadow', cs.boxShadow);

  target.dataset.dfpThemed = '1';
  return true;
}

export type StickyCtaOpts = {
  doc?: Document;
  /** The in-flow CTA whose visibility drives the bar (observed). */
  inlineCta: HTMLElement;
  label: string;
  onClick: () => void;
  /** Called with the freshly-built sticky button so the caller can
   *  mirror the theme style + warm the prefetch on it too. */
  onMount?: (cta: HTMLButtonElement) => void;
  /** LA FOTO DEL DISEÑO DEL CLIENTE, si en este navegador quedó uno
   *  (portada-del-cliente.ts). Se pinta a la izquierda del botón: la
   *  barra sale justo cuando el cliente ha dejado atrás la vitrina y
   *  está leyendo la descripción, y ahí lo único que le recuerda que
   *  ESTO ya es suyo es verlo. Sin diseño guardado no se pinta nada y
   *  la barra es la de siempre. */
  miniatura?: string | null;
};

/** Append a fixed bottom-bar copy of the CTA that fades in whenever
 *  the in-flow `inlineCta` is out of view (not reached yet or scrolled
 *  past).  Idempotent — a
 *  second call is a no-op (one bar per page). */
export function mountStickyCta(opts: StickyCtaOpts): void {
  const doc = opts.doc ?? document;
  if (doc.querySelector('.dfp-sticky-cta-bar')) return;

  const bar = doc.createElement('div');
  bar.className = 'dfp-sticky-cta-bar';
  bar.innerHTML = `
    <button type="button" class="dfp-trigger-cta dfp-sticky-cta">
      <span class="dfp-trigger-cta-label">${escHtml(opts.label)}</span>
      <span class="dfp-trigger-cta-arrow" aria-hidden="true">${TRIGGER_CHEVRON}</span>
    </button>`;
  // La miniatura se inserta como NODO y con el src puesto a mano: es una
  // data URL de decenas de kB y meterla en el innerHTML de arriba la
  // haría pasar por el parseador de HTML sin necesidad.
  if (opts.miniatura && opts.miniatura.startsWith('data:image/')) {
    const im = doc.createElement('img');
    im.className = 'dfp-sticky-thumb';
    im.alt = '';
    im.src = opts.miniatura;
    im.addEventListener('click', opts.onClick);
    bar.insertBefore(im, bar.firstChild);
  }
  doc.body.appendChild(bar);

  const cta = bar.querySelector<HTMLButtonElement>('.dfp-sticky-cta')!;
  cta.addEventListener('click', opts.onClick);
  opts.onMount?.(cta);

  const view = doc.defaultView;
  if (view && 'IntersectionObserver' in view) {
    // The bar is up WHENEVER the in-flow CTA is out of view — both when
    // the customer has scrolled past it AND when it's still below the
    // fold. It used to rise only once the button was overhead, so a
    // phone that lands on the PDP from an Instagram ad (gallery, chips
    // and promo badge first: the button sat at 1.064 px on an 812 px
    // screen, 1.232 on the 3D map) saw no way to personalise at all
    // until it scrolled. When the in-flow button comes into view the
    // bar steps aside, so there are never two on screen.
    const io = new view.IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          bar.classList.toggle('is-visible', !e.isIntersecting);
        }
      },
      { threshold: 0 },
    );
    io.observe(opts.inlineCta);
  } else {
    // No IntersectionObserver — keep the bar permanently available.
    bar.classList.add('is-visible');
  }
}
